import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Crown, Ban, Plus, Minus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

function UserStatusBadge({ status }) {
  const cls =
    status === "banned"
      ? "bg-red-500/10 text-red-400 border-red-500/20"
      : "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
  return (
    <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-sm border ${cls}`}>
      {status === "banned" ? "BANNED" : "ACTIVE"}
    </span>
  );
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [adjustOpen, setAdjustOpen] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState(0);
  const [grantOpen, setGrantOpen] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/users?q=${encodeURIComponent(q)}&page_size=100`);
      setUsers(r.data.items);
      setTotal(r.data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/plans").then((r) => setPlans(r.data));
    // Auto-refresh every 15s so new users appear without reloading the page.
    const id = setInterval(() => {
      load();
    }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doAdjust = async () => {
    try {
      await api.post(`/users/${adjustOpen.telegram_id}/credits`, {
        amount: Number(adjustAmount),
      });
      toast.success("Créditos ajustados");
      setAdjustOpen(null);
      setAdjustAmount(0);
      load();
    } catch (e) {
      toast.error("Error");
    }
  };

  const doBan = async (u) => {
    const newStatus = u.status === "banned" ? "active" : "banned";
    try {
      await api.post(`/users/${u.telegram_id}/status`, { status: newStatus });
      toast.success(newStatus === "banned" ? "Usuario bloqueado" : "Usuario reactivado");
      load();
    } catch (e) {
      toast.error("Error");
    }
  };

  const doGrantPlan = async () => {
    if (!selectedPlan) return;
    try {
      await api.post(`/users/${grantOpen.telegram_id}/membership`, {
        plan_id: selectedPlan,
      });
      toast.success("Membresía otorgada");
      setGrantOpen(null);
      setSelectedPlan(null);
      load();
    } catch (e) {
      toast.error("Error");
    }
  };

  const isActiveMembership = (u) => {
    if (!u.membership_expires_at) return false;
    return new Date(u.membership_expires_at) > new Date();
  };

  const trialRemainingMin = (u) => {
    if (!u.free_trial_expires_at) return 0;
    const diff = (new Date(u.free_trial_expires_at) - new Date()) / 60000;
    return diff > 0 ? Math.ceil(diff) : 0;
  };

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="overline mb-2">Base de usuarios</div>
          <h1 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight">Usuarios</h1>
          <p className="text-sm text-zinc-500 mt-2">
            {total} usuarios en tu bot de Telegram.{" "}
            <span className="inline-flex items-center gap-1.5 text-emerald-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              En vivo · se actualiza cada 15s
            </span>
          </p>
        </div>
        <Button
          onClick={load}
          variant="outline"
          size="sm"
          data-testid="users-refresh-btn"
          className="border-zinc-800 hover:bg-zinc-900 rounded-sm"
        >
          <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refrescar
        </Button>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Buscar por @usuario, nombre o ID Telegram"
            data-testid="users-search-input"
            className="bg-zinc-950 border-zinc-800 rounded-sm pl-9 h-10"
          />
        </div>
        <Button onClick={load} data-testid="users-search-button" className="bg-zinc-800 hover:bg-zinc-700 rounded-sm">
          Buscar
        </Button>
      </div>

      <div className="border border-zinc-800 rounded-sm overflow-hidden bg-zinc-900/50">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 bg-black/40">
                <th className="text-left px-4 py-3 overline">Usuario</th>
                <th className="text-left px-4 py-3 overline">ID Telegram</th>
                <th className="text-right px-4 py-3 overline">Créditos</th>
                <th className="text-left px-4 py-3 overline">Membresía</th>
                <th className="text-right px-4 py-3 overline">Referidos</th>
                <th className="text-right px-4 py-3 overline">Msgs</th>
                <th className="text-left px-4 py-3 overline">Estado</th>
                <th className="text-left px-4 py-3 overline">Último activo</th>
                <th className="text-right px-4 py-3 overline">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9} className="p-6 text-center text-zinc-500">
                    Cargando…
                  </td>
                </tr>
              )}
              {!loading && users.length === 0 && (
                <tr>
                  <td colSpan={9} className="p-10 text-center text-zinc-500">
                    Aún no hay usuarios. Cuando alguien escriba a tu bot aparecerá aquí.
                  </td>
                </tr>
              )}
              {users.map((u) => (
                <tr
                  key={u.telegram_id}
                  className="border-b border-zinc-900 hover:bg-zinc-900/40"
                  data-testid={`user-row-${u.telegram_id}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium">
                      {u.first_name || "—"} {u.last_name || ""}
                    </div>
                    <div className="text-xs text-zinc-500 font-mono">
                      {u.username ? `@${u.username}` : "(sin username)"}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{u.telegram_id}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold">{u.credits}</td>
                  <td className="px-4 py-3">
                    {isActiveMembership(u) ? (
                      <span className="flex items-center gap-1 text-amber-400 text-xs font-mono">
                        <Crown size={12} />
                        Premium · vence {u.membership_expires_at?.slice(0, 10)}
                      </span>
                    ) : trialRemainingMin(u) > 0 ? (
                      <span className="flex items-center gap-1 text-emerald-400 text-xs font-mono" data-testid={`trial-badge-${u.telegram_id}`}>
                        🎁 Trial · {trialRemainingMin(u)}m restantes
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-600">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="text-xs font-mono font-bold text-[#2AABEE]">
                      {u.referrals_count || 0}
                    </div>
                    {u.referral_credits_earned ? (
                      <div className="text-[10px] text-zinc-500 font-mono">
                        +{u.referral_credits_earned} cr.
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-zinc-400">
                    {u.messages_count || 0}
                  </td>
                  <td className="px-4 py-3">
                    <UserStatusBadge status={u.status} />
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500">
                    {new Date(u.last_active).toLocaleString("es-MX")}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs hover:bg-zinc-800 rounded-sm"
                        onClick={() => {
                          setAdjustOpen(u);
                          setAdjustAmount(0);
                        }}
                        data-testid={`adjust-credits-${u.telegram_id}`}
                      >
                        <Plus size={12} className="mr-1" />
                        Créditos
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs hover:bg-amber-500/10 text-amber-400 rounded-sm"
                        onClick={() => {
                          setGrantOpen(u);
                          setSelectedPlan(null);
                        }}
                        data-testid={`grant-premium-${u.telegram_id}`}
                      >
                        <Crown size={12} className="mr-1" />
                        Premium
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs hover:bg-red-500/10 text-red-400 rounded-sm"
                        onClick={() => doBan(u)}
                        data-testid={`ban-user-${u.telegram_id}`}
                      >
                        <Ban size={12} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Adjust credits dialog */}
      <Dialog open={!!adjustOpen} onOpenChange={() => setAdjustOpen(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display">
              Ajustar créditos — {adjustOpen?.first_name || adjustOpen?.telegram_id}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-zinc-400">
              Saldo actual: <span className="font-mono font-bold text-white">{adjustOpen?.credits}</span>
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">
                Cantidad (positiva para agregar, negativa para quitar)
              </label>
              <Input
                type="number"
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
                data-testid="adjust-credits-input"
                className="bg-zinc-950 border-zinc-800 rounded-sm"
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setAdjustAmount(50)}>+50</Button>
              <Button size="sm" variant="outline" onClick={() => setAdjustAmount(100)}>+100</Button>
              <Button size="sm" variant="outline" onClick={() => setAdjustAmount(500)}>+500</Button>
              <Button size="sm" variant="outline" onClick={() => setAdjustAmount(-50)}>-50</Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdjustOpen(null)}>Cancelar</Button>
            <Button onClick={doAdjust} data-testid="confirm-adjust-credits" className="bg-[#2AABEE] hover:bg-[#1C93D4] rounded-sm">
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant premium dialog */}
      <Dialog open={!!grantOpen} onOpenChange={() => setGrantOpen(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display">
              Otorgar membresía — {grantOpen?.first_name || grantOpen?.telegram_id}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-xs text-zinc-500 mb-1 block">Elige un plan</label>
            <Select value={selectedPlan} onValueChange={setSelectedPlan}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="grant-plan-select">
                <SelectValue placeholder="Selecciona un plan" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 rounded-sm">
                {plans.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`plan-option-${p.id}`}>
                    {p.name} · {p.duration_days}d · ${p.price_usd} USD
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGrantOpen(null)}>Cancelar</Button>
            <Button
              onClick={doGrantPlan}
              data-testid="confirm-grant-plan"
              disabled={!selectedPlan}
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm"
            >
              Otorgar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
